/**
 * SharePassportModal
 * Modal premium para compartir el pasaporte: genera URL pública/privada,
 * muestra una mini-previsualización, y permite crear imágenes para
 * Stories (9:16) y tarjeta cuadrada (1:1).
 */

import React, { useState, useRef, useCallback } from "react";
import {
	Box,
	Typography,
	IconButton,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	TextField,
	InputAdornment,
	Radio,
	RadioGroup,
	FormControlLabel,
	FormControl,
	CircularProgress,
} from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import LinkIcon from "@mui/icons-material/Link";
import html2canvas from "html2canvas";
import { LuPlane } from "react-icons/lu";
import WorldIcon from "../../assets/passport/world.png";
import Leather from "../../assets/passport/leather.png";

// ─── Paleta proyecto guia (lila) — solo interfaz del modal ───
const PRIMARY = "#6366F1";
const PRIMARY_LIGHT = "#8B5CF6";
const NAVY = "#0f1a2e";
const NAVY_MID = "#162240";

// ─── Paleta pasaporte (dorado) — mini preview e imágenes generadas ───
const GOLD = "#c9a84c";

// ─── Helpers ───
const downloadBlob = (blob, filename) => {
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
};

// ─── Componente de previsualización mini (dentro del modal) — estilo pasaporte dorado ───
const MiniPreview = ({ userName, countries }) => {
	return (
		<Box
			sx={{
				width: "100%",
				borderRadius: 2.5,
				overflow: "hidden",
				border: "1px solid rgba(201,168,76,0.18)",
				background: `linear-gradient(170deg, ${NAVY} 0%, ${NAVY_MID} 50%, #1a2a4e 100%)`,
				position: "relative",
				p: 2.5,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				gap: 1.5,
			}}
		>
			{/* Leather texture overlay */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					backgroundImage: `url(${Leather})`,
					backgroundSize: "cover",
					opacity: 0.12,
					pointerEvents: "none",
				}}
			/>
			{/* Gold border */}
			<Box
				sx={{
					position: "absolute",
					inset: 6,
					border: "1px solid rgba(201,168,76,0.12)",
					borderRadius: 2,
					pointerEvents: "none",
				}}
			/>

			{/* World icon small */}
			<Box
				component="img"
				src={WorldIcon}
				alt=""
				sx={{ width: 36, height: 36, opacity: 0.8, position: "relative" }}
			/>

			<Typography
				sx={{
					color: GOLD,
					fontSize: 13,
					fontWeight: 700,
					letterSpacing: "0.12em",
					textTransform: "uppercase",
					position: "relative",
					textShadow: "0 1px 3px rgba(0,0,0,0.4)",
				}}
			>
				Mi Pasaporte
			</Typography>

			<Typography
				sx={{
					color: "rgba(201,168,76,0.8)",
					fontSize: 11,
					fontWeight: 500,
					position: "relative",
					letterSpacing: "0.03em",
				}}
			>
				{userName}
			</Typography>

			<Typography
				sx={{
					color: "rgba(201,168,76,0.7)",
					fontSize: 12,
					fontWeight: 500,
					position: "relative",
					letterSpacing: "0.04em",
					mt: 0.5,
				}}
			>
				{countries.length === 0
					? "Sin sellos aún"
					: countries.length === 1
					? "1 destino"
					: `${countries.length} destinos`}
			</Typography>
		</Box>
	);
};

// ──────────────────────────────────────
// Canvas renderizado para generar imágenes
// ──────────────────────────────────────

/**
 * Genera un canvas off-screen con html2canvas.
 * @param {HTMLElement} element
 * @param {number} width
 * @param {number} height
 * @returns {Promise<Blob>}
 */
const captureElement = async (element, scale = 2) => {
	const canvas = await html2canvas(element, {
		scale,
		useCORS: true,
		allowTaint: true,
		backgroundColor: null,
		logging: false,
	});
	return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
};

// ─── Plantilla Stories (9:16) — igual que la portada del pasaporte ───
const StoriesTemplate = React.forwardRef(({ userName, countries }, ref) => {
	const count = countries.length;
	return (
		<Box
			ref={ref}
			sx={{
				width: 1080,
				height: 1920,
				position: "fixed",
				left: -9999,
				top: 0,
				background: `linear-gradient(170deg, ${NAVY} 0%, ${NAVY_MID} 35%, #1a2a4e 65%, #111d35 100%)`,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				fontFamily: "'Playfair Display', Georgia, serif",
				overflow: "hidden",
			}}
		>
			{/* Leather */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					backgroundImage: `url(${Leather})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
					opacity: 0.2,
					pointerEvents: "none",
				}}
			/>
			{/* Marco dorado */}
			<Box
				sx={{
					position: "absolute",
					inset: 50,
					border: "2px solid rgba(201,168,76,0.2)",
					borderRadius: "12px",
					boxShadow: "inset 0 0 30px rgba(0,0,0,0.15)",
					pointerEvents: "none",
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					inset: 58,
					border: "1px solid rgba(201,168,76,0.1)",
					borderRadius: "8px",
					pointerEvents: "none",
				}}
			/>
			{/* Esquinas filigrana */}
			{["top-left", "top-right", "bottom-left", "bottom-right"].map((corner) => {
				const isTop = corner.includes("top");
				const isLeft = corner.includes("left");
				return (
					<Box
						key={corner}
						sx={{
							position: "absolute",
							[isTop ? "top" : "bottom"]: 80,
							[isLeft ? "left" : "right"]: 80,
							width: 40,
							height: 40,
							pointerEvents: "none",
						}}
					>
						<Box
							sx={{
								position: "absolute",
								[isTop ? "top" : "bottom"]: 0,
								[isLeft ? "left" : "right"]: 0,
								width: 40,
								height: "2px",
								background: `linear-gradient(${isLeft ? "90deg" : "270deg"}, rgba(201,168,76,0.45), transparent)`,
							}}
						/>
						<Box
							sx={{
								position: "absolute",
								[isTop ? "top" : "bottom"]: 0,
								[isLeft ? "left" : "right"]: 0,
								width: "2px",
								height: 40,
								background: `linear-gradient(${isTop ? "180deg" : "0deg"}, rgba(201,168,76,0.45), transparent)`,
							}}
						/>
						<Box
							sx={{
								position: "absolute",
								[isTop ? "top" : "bottom"]: -4,
								[isLeft ? "left" : "right"]: -4,
								width: 8,
								height: 8,
								transform: "rotate(45deg)",
								backgroundColor: "rgba(201,168,76,0.2)",
							}}
						/>
					</Box>
				);
			})}

			{/* Línea decorativa superior */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4, position: "relative" }}>
				<Box sx={{ width: 70, height: "2px", background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.35))" }} />
				<Box sx={{ width: 8, height: 8, transform: "rotate(45deg)", backgroundColor: "rgba(201,168,76,0.25)" }} />
				<Box sx={{ width: 70, height: "2px", background: "linear-gradient(90deg, rgba(201,168,76,0.35), transparent)" }} />
			</Box>

			<Box sx={{ fontSize: 28, color: "rgba(201,168,76,0.85)", textTransform: "uppercase", letterSpacing: "0.45em", fontWeight: 500, mb: 4, position: "relative", textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>
				Viajes Molones
			</Box>

			{/* Emblema central (como portada) */}
			<Box
				sx={{
					width: 220,
					height: 220,
					borderRadius: "50%",
					border: "2px solid rgba(201,168,76,0.22)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					mb: 4,
					position: "relative",
					background: "radial-gradient(circle, rgba(201,168,76,0.03) 0%, transparent 70%)",
					boxShadow: "0 2px 4px rgba(0,0,0,0.3), inset 0 1px 1px rgba(201,168,76,0.05), 0 0 40px rgba(201,168,76,0.04)",
				}}
			>
				<Box sx={{ position: "absolute", inset: 12, borderRadius: "50%", border: "2px dashed rgba(201,168,76,0.12)" }} />
				<Box sx={{ position: "absolute", inset: -12, borderRadius: "50%", border: "1px solid rgba(201,168,76,0.08)" }} />
				{[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
					<Box
						key={deg}
						sx={{
							position: "absolute",
							width: deg % 90 === 0 ? 6 : 4,
							height: deg % 90 === 0 ? 6 : 4,
							borderRadius: "50%",
							backgroundColor: `rgba(201,168,76,${deg % 90 === 0 ? 0.25 : 0.12})`,
							top: "50%",
							left: "50%",
							transform: `rotate(${deg}deg) translateY(-115px) translate(-50%, -50%)`,
						}}
					/>
				))}
				<Box component="img" src={WorldIcon} alt="" sx={{ width: 170, height: 170, objectFit: "contain", filter: "drop-shadow(0 2px 15px rgba(201,168,76,0.2))" }} />
			</Box>

			<Box sx={{ fontSize: 72, fontWeight: 700, color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase", mb: 1, position: "relative", textShadow: "0 1px 0 rgba(201,168,76,0.3), 0 2px 4px rgba(0,0,0,0.3), 0 0 20px rgba(201,168,76,0.08)" }}>
				PASAPORTE
			</Box>

			<Box sx={{ fontSize: 22, color: "rgba(201,168,76,0.78)", textTransform: "uppercase", letterSpacing: "0.2em", mb: 4, position: "relative", textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>
				Passport · パスポート · Passeport
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", mb: 4 }}>
				<Box sx={{ width: 80, height: "2px", background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.25), transparent)" }} />
				<Box sx={{ width: 50, height: "2px", background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.15), transparent)" }} />
			</Box>

			<Box sx={{ textAlign: "center", mb: 4, position: "relative" }}>
				<Box sx={{ fontSize: 18, color: "rgba(201,168,76,0.8)", textTransform: "uppercase", letterSpacing: "0.25em", mb: 1, textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>
					Titular / Holder
				</Box>
				<Box sx={{ fontSize: 42, color: "rgba(201,168,76,0.95)", fontWeight: 600, letterSpacing: "0.05em", textShadow: "0 1px 3px rgba(0,0,0,0.4), 0 0 10px rgba(0,0,0,0.15)" }}>
					{userName}
				</Box>
			</Box>

			{/* Contador de sellos (como portada) */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 2,
					px: 5,
					py: 1.5,
					borderRadius: "28px",
					border: "2px solid rgba(201,168,76,0.12)",
					backgroundColor: "rgba(201,168,76,0.03)",
					boxShadow: "inset 0 0 12px rgba(0,0,0,0.1)",
					position: "relative",
				}}
			>
				<LuPlane size={36} color="rgba(201, 168, 76, 0.85)" style={{ flexShrink: 0 }} />
				<Box sx={{ fontSize: 26, color: "rgba(201,168,76,0.88)", letterSpacing: "0.03em", fontWeight: 500, textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>
					{count === 0 ? "Sin sellos aún" : count === 1 ? "1 sello" : `${count} sellos`}
				</Box>
			</Box>
		</Box>
	);
});

// ─── Plantilla Tarjeta (1:1) — igual que la portada del pasaporte ───
const CardTemplate = React.forwardRef(({ userName, countries }, ref) => {
	const count = countries.length;
	return (
		<Box
			ref={ref}
			sx={{
				width: 1080,
				height: 1080,
				position: "fixed",
				left: -9999,
				top: 0,
				background: `linear-gradient(170deg, ${NAVY} 0%, ${NAVY_MID} 40%, #1a2a4e 100%)`,
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				fontFamily: "'Playfair Display', Georgia, serif",
				overflow: "hidden",
			}}
		>
			{/* Leather */}
			<Box
				sx={{
					position: "absolute",
					inset: 0,
					backgroundImage: `url(${Leather})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
					opacity: 0.2,
					pointerEvents: "none",
				}}
			/>
			{/* Marco dorado */}
			<Box
				sx={{
					position: "absolute",
					inset: 40,
					border: "2px solid rgba(201,168,76,0.2)",
					borderRadius: "12px",
					boxShadow: "inset 0 0 25px rgba(0,0,0,0.15)",
					pointerEvents: "none",
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					inset: 48,
					border: "1px solid rgba(201,168,76,0.1)",
					borderRadius: "8px",
					pointerEvents: "none",
				}}
			/>
			{/* Esquinas filigrana */}
			{["top-left", "top-right", "bottom-left", "bottom-right"].map((corner) => {
				const isTop = corner.includes("top");
				const isLeft = corner.includes("left");
				return (
					<Box
						key={corner}
						sx={{
							position: "absolute",
							[isTop ? "top" : "bottom"]: 60,
							[isLeft ? "left" : "right"]: 60,
							width: 32,
							height: 32,
							pointerEvents: "none",
						}}
					>
						<Box
							sx={{
								position: "absolute",
								[isTop ? "top" : "bottom"]: 0,
								[isLeft ? "left" : "right"]: 0,
								width: 32,
								height: "2px",
								background: `linear-gradient(${isLeft ? "90deg" : "270deg"}, rgba(201,168,76,0.45), transparent)`,
							}}
						/>
						<Box
							sx={{
								position: "absolute",
								[isTop ? "top" : "bottom"]: 0,
								[isLeft ? "left" : "right"]: 0,
								width: "2px",
								height: 32,
								background: `linear-gradient(${isTop ? "180deg" : "0deg"}, rgba(201,168,76,0.45), transparent)`,
							}}
						/>
						<Box
							sx={{
								position: "absolute",
								[isTop ? "top" : "bottom"]: -3,
								[isLeft ? "left" : "right"]: -3,
								width: 6,
								height: 6,
								transform: "rotate(45deg)",
								backgroundColor: "rgba(201,168,76,0.2)",
							}}
						/>
					</Box>
				);
			})}

			{/* Línea decorativa superior */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, position: "relative" }}>
				<Box sx={{ width: 50, height: "2px", background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.35))" }} />
				<Box sx={{ width: 6, height: 6, transform: "rotate(45deg)", backgroundColor: "rgba(201,168,76,0.25)" }} />
				<Box sx={{ width: 50, height: "2px", background: "linear-gradient(90deg, rgba(201,168,76,0.35), transparent)" }} />
			</Box>

			<Box sx={{ fontSize: 20, color: "rgba(201,168,76,0.85)", textTransform: "uppercase", letterSpacing: "0.45em", fontWeight: 500, mb: 2, position: "relative", textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>
				Viajes Molones
			</Box>

			{/* Emblema central (como portada) */}
			<Box
				sx={{
					width: 160,
					height: 160,
					borderRadius: "50%",
					border: "2px solid rgba(201,168,76,0.22)",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					mb: 2,
					position: "relative",
					background: "radial-gradient(circle, rgba(201,168,76,0.03) 0%, transparent 70%)",
					boxShadow: "0 2px 4px rgba(0,0,0,0.3), inset 0 1px 1px rgba(201,168,76,0.05), 0 0 30px rgba(201,168,76,0.04)",
				}}
			>
				<Box sx={{ position: "absolute", inset: 10, borderRadius: "50%", border: "1px dashed rgba(201,168,76,0.12)" }} />
				<Box sx={{ position: "absolute", inset: -10, borderRadius: "50%", border: "1px solid rgba(201,168,76,0.08)" }} />
				{[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
					<Box
						key={deg}
						sx={{
							position: "absolute",
							width: deg % 90 === 0 ? 4 : 3,
							height: deg % 90 === 0 ? 4 : 3,
							borderRadius: "50%",
							backgroundColor: `rgba(201,168,76,${deg % 90 === 0 ? 0.25 : 0.12})`,
							top: "50%",
							left: "50%",
							transform: `rotate(${deg}deg) translateY(-88px) translate(-50%, -50%)`,
						}}
					/>
				))}
				<Box component="img" src={WorldIcon} alt="" sx={{ width: 120, height: 120, objectFit: "contain", filter: "drop-shadow(0 2px 12px rgba(201,168,76,0.2))" }} />
			</Box>

			<Box sx={{ fontSize: 52, fontWeight: 700, color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase", mb: 0.5, position: "relative", textShadow: "0 1px 0 rgba(201,168,76,0.3), 0 2px 4px rgba(0,0,0,0.3), 0 0 16px rgba(201,168,76,0.08)" }}>
				PASAPORTE
			</Box>

			<Box sx={{ fontSize: 16, color: "rgba(201,168,76,0.78)", textTransform: "uppercase", letterSpacing: "0.2em", mb: 2, position: "relative", textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>
				Passport · パスポート · Passeport
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "3px", mb: 2 }}>
				<Box sx={{ width: 60, height: "1px", background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.25), transparent)" }} />
				<Box sx={{ width: 40, height: "1px", background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.15), transparent)" }} />
			</Box>

			<Box sx={{ textAlign: "center", mb: 2, position: "relative" }}>
				<Box sx={{ fontSize: 14, color: "rgba(201,168,76,0.8)", textTransform: "uppercase", letterSpacing: "0.25em", mb: 0.5, textShadow: "0 1px 2px rgba(0,0,0,0.4)" }}>
					Titular / Holder
				</Box>
				<Box sx={{ fontSize: 30, color: "rgba(201,168,76,0.95)", fontWeight: 600, letterSpacing: "0.05em", textShadow: "0 1px 3px rgba(0,0,0,0.4), 0 0 8px rgba(0,0,0,0.15)" }}>
					{userName}
				</Box>
			</Box>

			{/* Contador de sellos (como portada) */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1.5,
					px: 4,
					py: 1,
					borderRadius: "24px",
					border: "2px solid rgba(201,168,76,0.12)",
					backgroundColor: "rgba(201,168,76,0.03)",
					boxShadow: "inset 0 0 10px rgba(0,0,0,0.1)",
					position: "relative",
				}}
			>
				<LuPlane size={28} color="rgba(201, 168, 76, 0.85)" style={{ flexShrink: 0 }} />
				<Box sx={{ fontSize: 20, color: "rgba(201,168,76,0.88)", letterSpacing: "0.03em", fontWeight: 500, textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>
					{count === 0 ? "Sin sellos aún" : count === 1 ? "1 sello" : `${count} sellos`}
				</Box>
			</Box>
		</Box>
	);
});

// ══════════════════════════════════════
// Componente principal
// ══════════════════════════════════════

const SharePassportModal = ({
	open,
	onClose,
	passport,
	countries = [],
	onSharePassport,
	onSnackbar,
	onShareSuccess,
}) => {
	const userName = passport?.displayName || "Viajero";
	const [shareIsPublic, setShareIsPublic] = useState(false);
	const [shareLoading, setShareLoading] = useState(false);
	const [sharedUrl, setSharedUrl] = useState(null);
	const [shareCopied, setShareCopied] = useState(false);
	const [generatingStory, setGeneratingStory] = useState(false);
	const [generatingCard, setGeneratingCard] = useState(false);
	const [overwriteConfirmOpen, setOverwriteConfirmOpen] = useState(false);

	const storiesRef = useRef(null);
	const cardRef = useRef(null);

	// Enlace actual (si ya compartió antes): un solo enlace por pasaporte
	const origin = typeof window !== "undefined" ? window.location.origin : "";
	const existingSharedUrl = passport?.shareToken
		? `${origin}/passport/view/${passport.shareToken}`
		: null;
	const existingShareIsPublic = passport?.shareIsPublic ?? false;
	const hasExistingLink = !!existingSharedUrl;
	// Una sola URL a mostrar: la recién generada o la actual
	const displayUrl = sharedUrl ?? existingSharedUrl;
	const displayIsPublic = sharedUrl != null ? shareIsPublic : existingShareIsPublic;

	const handleClose = () => {
		onClose();
	};

	const doGenerateUrl = useCallback(async () => {
		setShareLoading(true);
		setShareCopied(false);
		try {
			const result = await onSharePassport(shareIsPublic);
			if (result && result.url) {
				setSharedUrl(result.url);
				onShareSuccess?.();
				onSnackbar?.({
					open: true,
					message: "URL generada correctamente",
					severity: "success",
				});
			} else {
				onSnackbar?.({
					open: true,
					message: "Error al generar la URL",
					severity: "error",
				});
			}
		} catch {
			onSnackbar?.({
				open: true,
				message: "Error al generar la URL",
				severity: "error",
			});
		} finally {
			setShareLoading(false);
			setOverwriteConfirmOpen(false);
		}
	}, [shareIsPublic, onSharePassport, onSnackbar, onShareSuccess]);

	const handleGenerateUrl = useCallback(() => {
		if (hasExistingLink) {
			setOverwriteConfirmOpen(true);
			return;
		}
		doGenerateUrl();
	}, [hasExistingLink, doGenerateUrl]);

	const handleCopyUrl = useCallback(async () => {
		if (!displayUrl) return;
		try {
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(displayUrl);
			} else {
				const textarea = document.createElement("textarea");
				textarea.value = displayUrl;
				textarea.style.position = "fixed";
				textarea.style.opacity = "0";
				textarea.setAttribute("readonly", "");
				document.body.appendChild(textarea);
				textarea.select();
				document.execCommand("copy");
				document.body.removeChild(textarea);
			}
			setShareCopied(true);
			onSnackbar?.({
				open: true,
				message: "URL copiada al portapapeles",
				severity: "success",
			});
			setTimeout(() => setShareCopied(false), 2500);
		} catch {
			onSnackbar?.({
				open: true,
				message: "No se pudo copiar. Copia la URL manualmente.",
				severity: "warning",
			});
		}
	}, [displayUrl, onSnackbar]);

	const handleGenerateStory = useCallback(async () => {
		if (!storiesRef.current) return;
		setGeneratingStory(true);
		try {
			const blob = await captureElement(storiesRef.current, 1);
			if (blob) downloadBlob(blob, "mi-pasaporte-stories.png");
		} catch (err) {
			console.error("Error generando story:", err);
			onSnackbar?.({
				open: true,
				message: "Error al generar la imagen",
				severity: "error",
			});
		} finally {
			setGeneratingStory(false);
		}
	}, [onSnackbar]);

	const handleGenerateCard = useCallback(async () => {
		if (!cardRef.current) return;
		setGeneratingCard(true);
		try {
			const blob = await captureElement(cardRef.current, 1);
			if (blob) downloadBlob(blob, "mi-pasaporte-tarjeta.png");
		} catch (err) {
			console.error("Error generando tarjeta:", err);
			onSnackbar?.({
				open: true,
				message: "Error al generar la imagen",
				severity: "error",
			});
		} finally {
			setGeneratingCard(false);
		}
	}, [onSnackbar]);

	return (
		<>
			{/* Plantillas off-screen para html2canvas */}
			{open && (
				<>
					<StoriesTemplate
						ref={storiesRef}
						userName={userName}
						countries={countries}
					/>
					<CardTemplate
						ref={cardRef}
						userName={userName}
						countries={countries}
					/>
				</>
			)}

			<Dialog
				open={open}
				onClose={handleClose}
				PaperProps={{
					sx: {
						borderRadius: 3,
						maxWidth: 460,
						width: "100%",
						m: 2,
						p: 0,
						overflow: "visible",
						maxHeight: "90vh",
					},
				}}
			>
				<DialogContent
					sx={{
						p: 0,
						"&:first-of-type": { pt: 0 },
						overflowY: "auto",
					}}
				>
					{/* ─── Header interfaz guia (lila) ─── */}
					<Box
						sx={{
							background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_LIGHT} 100%)`,
							boxShadow: "0 4px 16px rgba(99, 102, 241, 0.35)",
							borderRadius: "12px 12px 0 0",
							px: 3,
							pt: 3,
							pb: 2.5,
							position: "relative",
						}}
					>
						<IconButton
							onClick={handleClose}
							sx={{
								position: "absolute",
								top: 8,
								right: 8,
								color: "rgba(255,255,255,0.9)",
								"&:hover": { background: "rgba(255,255,255,0.12)" },
								zIndex: 2,
							}}
							size="small"
						>
							<CloseIcon fontSize="small" />
						</IconButton>

						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: 0.5,
							}}
						>
							<Typography
								sx={{
									color: "#fff",
									fontSize: 15,
									fontWeight: 700,
									letterSpacing: "0.1em",
									textTransform: "uppercase",
								}}
							>
								Compartir Pasaporte
							</Typography>
							<Typography
								sx={{
									color: "rgba(255,255,255,0.8)",
									fontSize: 10,
									letterSpacing: "0.08em",
								}}
							>
								Comparte tu colección de sellos
							</Typography>
						</Box>
					</Box>

					{/* ─── Body ─── */}
					<Box
						sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}
					>
						{/* ── Mini preview ── */}
						<MiniPreview userName={userName} countries={countries} />

						{/* ── Tipo de URL ── */}
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
							<Typography
								sx={{
									fontSize: 11,
									textTransform: "uppercase",
									letterSpacing: 0.5,
									color: "#94a3b8",
									fontWeight: 600,
								}}
							>
								Tipo de enlace
							</Typography>
							<FormControl>
								<RadioGroup
									value={shareIsPublic ? "public" : "private"}
									onChange={(e) => {
										setShareIsPublic(e.target.value === "public");
										setSharedUrl(null);
										setShareCopied(false);
									}}
									sx={{ gap: 1 }}
								>
									<FormControlLabel
										value="public"
										control={
											<Radio
												size="small"
												sx={{
													color: "#64748b",
													"&.Mui-checked": { color: PRIMARY },
													p: 0.5,
												}}
											/>
										}
										label={
											<Box sx={{ ml: 0.5 }}>
												<Typography
													sx={{
														fontSize: 13,
														fontWeight: 600,
														color: "#1e293b",
													}}
												>
													Pública
												</Typography>
												<Typography
													sx={{
														fontSize: 11,
														color: "#94a3b8",
														lineHeight: 1.4,
													}}
												>
													Cualquiera con el enlace puede verlo
												</Typography>
											</Box>
										}
										sx={{
											m: 0,
											py: 1,
											px: 1.5,
											borderRadius: 2,
											border: shareIsPublic
												? `1.5px solid ${PRIMARY}`
												: "1.5px solid rgba(148,163,184,0.15)",
											background: shareIsPublic
												? "rgba(99, 102, 241, 0.04)"
												: "transparent",
											transition: "all 0.2s ease",
											"&:hover": {
												background: shareIsPublic
													? "rgba(99, 102, 241, 0.06)"
													: "rgba(148,163,184,0.04)",
											},
										}}
									/>
									<FormControlLabel
										value="private"
										control={
											<Radio
												size="small"
												sx={{
													color: "#64748b",
													"&.Mui-checked": { color: PRIMARY },
													p: 0.5,
												}}
											/>
										}
										label={
											<Box sx={{ ml: 0.5 }}>
												<Typography
													sx={{
														fontSize: 13,
														fontWeight: 600,
														color: "#1e293b",
													}}
												>
													Privada
												</Typography>
												<Typography
													sx={{
														fontSize: 11,
														color: "#94a3b8",
														lineHeight: 1.4,
													}}
												>
													Solo tú (o quien tú decidas)
												</Typography>
											</Box>
										}
										sx={{
											m: 0,
											py: 1,
											px: 1.5,
											borderRadius: 2,
											border: !shareIsPublic
												? `1.5px solid ${PRIMARY}`
												: "1.5px solid rgba(148,163,184,0.15)",
											background: !shareIsPublic
												? "rgba(99, 102, 241, 0.04)"
												: "transparent",
											transition: "all 0.2s ease",
											"&:hover": {
												background: !shareIsPublic
													? "rgba(99, 102, 241, 0.06)"
													: "rgba(148,163,184,0.04)",
											},
										}}
									/>
								</RadioGroup>
							</FormControl>
						</Box>

						{/* ── Enlace actual (único bloque: existente o recién generado) ── */}
						{displayUrl && (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									gap: 1,
									p: 1.5,
									borderRadius: 2,
									background: "rgba(248,250,252,0.7)",
									border: "1px solid rgba(99, 102, 241, 0.12)",
								}}
							>
								<Typography
									sx={{
										fontSize: 11,
										fontWeight: 600,
										textTransform: "uppercase",
										letterSpacing: "0.05em",
										color: "#64748b",
									}}
								>
									Enlace actual
								</Typography>
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
									{displayIsPublic ? (
										<PublicIcon sx={{ fontSize: 16, color: "#64748b" }} />
									) : (
										<LockIcon sx={{ fontSize: 16, color: "#64748b" }} />
									)}
									<Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
										{displayIsPublic ? "Público" : "Privado"}
									</Typography>
								</Box>
								<TextField
									value={displayUrl}
									size="small"
									fullWidth
									InputProps={{
										readOnly: true,
										sx: { fontSize: 11.5 },
										endAdornment: (
											<InputAdornment position="end">
												<IconButton
													size="small"
													onClick={handleCopyUrl}
													title="Copiar"
													sx={{
														color: shareCopied ? "#16a34a" : "#64748b",
														"&:hover": {
															background: shareCopied ? "rgba(22,163,74,0.08)" : "rgba(100,116,139,0.08)",
														},
													}}
												>
													{shareCopied ? (
														<CheckCircleIcon sx={{ fontSize: 16 }} />
													) : (
														<ContentCopyIcon sx={{ fontSize: 16 }} />
													)}
												</IconButton>
											</InputAdornment>
										),
									}}
									sx={{
										"& .MuiOutlinedInput-root": {
											backgroundColor: "#fff",
											borderRadius: "8px",
											"& fieldset": { borderColor: "rgba(99, 102, 241, 0.15)" },
										},
									}}
								/>
								{shareCopied && (
									<Typography sx={{ fontSize: 11, color: "#16a34a", fontWeight: 500, display: "flex", alignItems: "center", gap: 0.5 }}>
										<CheckCircleIcon sx={{ fontSize: 13 }} />
										Enlace copiado
									</Typography>
								)}
								<Button
									variant="outlined"
									size="small"
									onClick={() => window.open(displayUrl, "_blank", "noopener,noreferrer")}
									sx={{
										width: "100%",
										alignSelf: "flex-start",
										textTransform: "none",
										fontSize: 12,
										fontWeight: 600,
										borderColor: `${PRIMARY}!important`,
										color: `${PRIMARY}!important`,
										"&:hover": {
											borderColor: PRIMARY,
											background: "rgba(99, 102, 241, 0.06)",
										},
									}}
								>
									Ver pasaporte
								</Button>
							</Box>
						)}

						{/* ── Aviso: generar de nuevo reemplaza el enlace actual ── */}
						{hasExistingLink && (
							<Box
								sx={{
									p: 1.5,
									borderRadius: 2,
									background: "rgba(245, 158, 11, 0.1)",
									border: "1px solid rgba(245, 158, 11, 0.4)",
								}}
							>
								<Typography
									sx={{
										fontSize: 12,
										fontWeight: 600,
										color: "#b45309",
										display: "flex",
										alignItems: "center",
										gap: 0.75,
									}}
								>
									<InfoOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
									Si generas de nuevo, se reemplazará el enlace actual. El enlace anterior dejará de funcionar.
								</Typography>
							</Box>
						)}

						{/* ── Generar URL button ── */}
						<Button
							variant={hasExistingLink ? "outlined" : "contained"}
							disableElevation
							disabled={shareLoading}
							onClick={handleGenerateUrl}
							startIcon={
								shareLoading ? (
									<CircularProgress size={14} sx={{ color: hasExistingLink ? PRIMARY : "#fff" }} />
								) : (
									<LinkIcon sx={{ fontSize: 16 }} />
								)
							}
							sx={
								hasExistingLink
									? {
											height: 40,
											borderRadius: 2,
											border: `2px solid ${PRIMARY}`,
											background: "transparent",
											color: PRIMARY,
											textTransform: "none",
											fontWeight: 600,
											fontSize: 13,
											letterSpacing: "0.02em",
											"&:hover": {
												border: `2px solid ${PRIMARY}`,
												background: "rgba(99, 102, 241, 0.08)",
											},
											"&.Mui-disabled": {
												borderColor: "rgba(99, 102, 241, 0.3)",
												color: "rgba(99, 102, 241, 0.5)",
											},
										}
									: {
											height: 40,
											borderRadius: 2,
											background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_LIGHT} 100%)`,
											color: "#fff",
											textTransform: "none",
											fontWeight: 600,
											fontSize: 13,
											letterSpacing: "0.02em",
											boxShadow: "0 4px 16px rgba(99, 102, 241, 0.35)",
											"&:hover": {
												background: `linear-gradient(135deg, #7577f3 0%, #9d6ff8 100%)`,
												boxShadow: "0 6px 20px rgba(99, 102, 241, 0.45)",
											},
											"&.Mui-disabled": {
												background: "rgba(99, 102, 241, 0.3)",
												color: "rgba(255,255,255,0.6)",
											},
										}
							}
						>
							{shareLoading ? "Generando..." : "Generar URL"}
						</Button>

						{/* ── Opciones visuales de compartir ── */}
						{displayUrl && (
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
								<Typography
									sx={{
										fontSize: 11,
										textTransform: "uppercase",
										letterSpacing: 0.5,
										color: "#94a3b8",
										fontWeight: 600,
									}}
								>
									Crear imagen para compartir
								</Typography>

								<Box sx={{ display: "flex", gap: 1.5 }}>
									{/* Stories button */}
									<Button
										variant="outlined"
										disabled={generatingStory}
										onClick={handleGenerateStory}
										startIcon={
											generatingStory ? (
												<CircularProgress size={14} />
											) : (
												<AutoStoriesIcon sx={{ fontSize: 16 }} />
											)
										}
										sx={{
											flex: 1,
											height: 60,
											borderRadius: 2,
											borderColor: "rgba(99, 102, 241, 0.2)",
											color: "#475569",
											textTransform: "none",
											fontWeight: 500,
											fontSize: 12,
											display: "flex",
											flexDirection: "column",
											gap: 0.25,
											lineHeight: 1.3,
											"&:hover": {
												borderColor: PRIMARY,
												background: "rgba(99, 102, 241, 0.04)",
											},
											"& .MuiButton-startIcon": {
												m: 0,
												mb: 0.25,
											},
										}}
									>
										<Box
											component="span"
											sx={{ fontSize: 12, fontWeight: 600 }}
										>
											{generatingStory ? "Creando..." : "Para Stories"}
										</Box>
										<Box
											component="span"
											sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 400 }}
										>
											Vertical 9:16
										</Box>
									</Button>

									{/* Card button */}
									<Button
										variant="outlined"
										disabled={generatingCard}
										onClick={handleGenerateCard}
										startIcon={
											generatingCard ? (
												<CircularProgress size={14} />
											) : (
												<CropSquareIcon sx={{ fontSize: 16 }} />
											)
										}
										sx={{
											flex: 1,
											height: 60,
											borderRadius: 2,
											borderColor: "rgba(99, 102, 241, 0.2)",
											color: "#475569",
											textTransform: "none",
											fontWeight: 500,
											fontSize: 12,
											display: "flex",
											flexDirection: "column",
											gap: 0.25,
											lineHeight: 1.3,
											"&:hover": {
												borderColor: PRIMARY,
												background: "rgba(99, 102, 241, 0.04)",
											},
											"& .MuiButton-startIcon": {
												m: 0,
												mb: 0.25,
											},
										}}
									>
										<Box
											component="span"
											sx={{ fontSize: 12, fontWeight: 600 }}
										>
											{generatingCard ? "Creando..." : "Tarjeta"}
										</Box>
										<Box
											component="span"
											sx={{ fontSize: 10, color: "#94a3b8", fontWeight: 400 }}
										>
											Cuadrada 1:1
										</Box>
									</Button>
								</Box>

								<Typography
									sx={{
										fontSize: 10,
										color: "#b0b8c4",
										textAlign: "center",
										lineHeight: 1.4,
										mt: 0.5,
									}}
								>
									Las imágenes se descargarán en tu dispositivo listas para
									subir a redes
								</Typography>
							</Box>
						)}
					</Box>
				</DialogContent>
			</Dialog>

			{/* Modal confirmar reemplazar enlace */}
			<Dialog
				open={overwriteConfirmOpen}
				onClose={() => !shareLoading && setOverwriteConfirmOpen(false)}
				PaperProps={{ sx: { borderRadius: 2 } }}
			>
				<DialogTitle sx={{ fontWeight: 700, fontSize: 18, color: NAVY }}>
					Reemplazar enlace
				</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>
						Se generará un nuevo enlace {shareIsPublic ? "público" : "privado"} y el actual dejará de funcionar. ¿Continuar?
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
					<Button
						onClick={() => setOverwriteConfirmOpen(false)}
						disabled={shareLoading}
						sx={{ textTransform: "none", fontSize: 14 }}
					>
						Cancelar
					</Button>
					<Button
						variant="contained"
						onClick={() => doGenerateUrl()}
						disabled={shareLoading}
						sx={{
							textTransform: "none",
							fontSize: 14,
							fontWeight: 600,
							background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_LIGHT} 100%)`,
							"&:hover": { background: `linear-gradient(135deg, #7577f3 0%, #9d6ff8 100%)` },
						}}
					>
						{shareLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Generar enlace"}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default SharePassportModal;
